import {
  ApolloError,
  QueryHookOptions,
  QueryResult,
  TypedDocumentNode,
  useQuery,
  useSuspenseQuery,
} from '@apollo/client';
import { gql } from 'graphql-tag';
import { MarketLocation, MarketLocationQuery, MarketLocationQueryVariables } from '../../generated/graphql';
import { ADDRESS_FIELDS, NULLABLE_MEASURE_FIELDS, PRODUCT_FIELDS } from '../../queries';

export type { Device, Machine, MarketLocation } from '../../generated/graphql';

const query: TypedDocumentNode<MarketLocationQuery, MarketLocationQueryVariables> = gql`
  query MarketLocation($id: ID!) {
    marketLocation(id: $id) {
      ...MarketLocationFields
      linkedMarketLocation {
        ...MarketLocationFields
      }
    }
  }
  fragment MarketLocationFields on MarketLocation {
    id
    type
    number
    marketLocationId
    measureLocationId
    name
    externalName
    products {
      ...ProductFields
    }
    machines {
      id
      name
      machineTechnology
      devices {
        externalName
        id
        internalId
        name
        tradingGroupId
        products {
          ...ProductFields
        }
        deadlines {
          type
          deadline {
            deadlineForEarliestForecast
            daysBeforeDelivery
            minutesBeforeIntradayDelivery
          }
        }
      }
      installedPower {
        ...NullableMeasureFields
      }
      address {
        ...AddressFields
      }
    }
    technologies
    machinesTotalOutput {
      ...NullableMeasureFields
    }
    contractPartner
    distributionNetworkOperator
    transmissionSystemOperator
    marketingStart
    marketingEnd
    permissions {
      seeForecast
      editForecast
      seeRevenue
    }
    billingTypes {
      year
      month
      isOpenBook
    }
    productServices {
      type
      begin
      end
    }
    forecastProperties {
      resolution
      editableMarkets
      target
      validFrom
      validTo
    }
  }
  ${PRODUCT_FIELDS}
  ${ADDRESS_FIELDS}
  ${NULLABLE_MEASURE_FIELDS}
`;

export function useMarketLocation(id: string): [MarketLocation | undefined, ApolloError | undefined] {
  const { data, error } = useSuspenseQuery(query, { variables: { id }, errorPolicy: 'all' });
  return [data?.marketLocation, error];
}

export function useMarketLocationSync(
  id: string,
  options?: QueryHookOptions<MarketLocationQuery, MarketLocationQueryVariables>,
): QueryResult<MarketLocationQuery, MarketLocationQueryVariables> {
  return useQuery<MarketLocationQuery, MarketLocationQueryVariables>(query, {
    variables: { id },
    ...options,
  });
}
